import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Box,
	CircularProgress,
	Paper,
	Typography,
	ThemeProvider,
	createTheme,
	Button,
} from "@material-ui/core";
import { DeleteOutline, Edit, FileCopy } from "@material-ui/icons";
import MaterialTable from "material-table";
import { TableIcons } from "../../../../utils/TableIcons";
import { LocalizationTheme } from "../../../../utils/utils";
import { useHistory, useLocation, Link } from "react-router-dom";
import DeleteConfirmationModal from "../../../Common/Modals/DeleteConfirmationModal";
import {
	deleteMessageTemplate,
	getBrands,
	getMessageTemplates,
	copyMessageTemplate,
} from "./services/messageTemplatesApiService";
import { getMessageTypeLabel } from "./utils/labels";
import { DisplayMessage } from "../../../../services/helpers";
import MessageTemplatesCopyModal from "./components/MessageTemplatesCopyModal";
import { Permissions } from "../../../../utils/Permissions";
import OutletsDropdown from '../../../Common/Dropdowns/OutletsDropdown.js';
import BrandsDropdown from '../../../Common/Dropdowns/BrandsDropdown.js';

const MessageTemplatesTable = (props) => {
	const {
		userPermissions,
		resources,
		accessibleMoboOutlets,
		outletId,
		objectType,
	} = props;

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedOutlet, setSelectedOutlet] = useState(outletId || 0);
	const [selectedBrand, setSelectedBrand] = useState(0);
	const [brands, setBrands] = useState([]);
	const [filteredBrands, setFilteredBrands] = useState([]);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [copyModalOpen, setCopyModalOpen] = useState(false);
	const [selectedTemplateToCopy, setSelectedTemplateToCopy] = useState(null);
	const [copyLoading, setCopyLoading] = useState(false);
	const [copyError, setCopyError] = useState(null);

	const history = useHistory();
	const location = useLocation();

	const fetchMessageTemplates = useCallback(async () => {
		setLoading(true);

		try {
			const response = await getMessageTemplates(selectedOutlet, selectedBrand);

			if (!response) {
				setData([]);
				return;
			}

			const mappedTemplates = response.map((template) => {
				const useCase = template.useCase || {};
				return {
					id: template.id,
					useCaseDescription: useCase.description || "",
					messageTypeText: getMessageTypeLabel(
						resources,
						useCase.messageType || 0
					),
					outletId: template.outletId || 0,
					outletDescription: getOutletDescription(template.outletId),
					brandId: template.brand?.id || 0,
					brand: template.brand?.description || "",
					language: template.language || "",
					isWaitsOnPremiseDependent: template.isWaitsOnPremiseDependent || false,
					isWaitsOnPremise: template.isWaitsOnPremise || false,
				};
			});

			setData(mappedTemplates);
		} catch (error) {
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [selectedOutlet, selectedBrand, resources]);

	function getOutletDescription(outletId) {
		if (!outletId || !accessibleMoboOutlets) return "";
		const outlet = accessibleMoboOutlets.find((o) => o.id === outletId);
		return outlet ? outlet.description : "";
	}

	useEffect(() => {
		const shouldRefresh = location.state?.refreshGrid || false;
		if (!data.length || shouldRefresh) {
			fetchMessageTemplates();

			if (shouldRefresh) {
				history.replace({
					pathname: location.pathname,
					state: {},
				});
			}
		}
	}, [data.length, location, fetchMessageTemplates, history]);

	useEffect(() => {
		if (data.length > 0) {
			fetchMessageTemplates();
		}
	}, [selectedOutlet, selectedBrand, fetchMessageTemplates]);

	useEffect(() => {
		const fetchBrands = async () => {
			const response = await getBrands(null);
			if (response) {
				setBrands(
					response.map((brand) => ({
						id: brand.id,
						description: brand.description,
						objectType: brand.objectType,
					}))
				);
			}
		};

		fetchBrands();
	}, [objectType]);

	useEffect(() => {
		const filteredBrands = filterBrandsByObjectType(objectType);
		setFilteredBrands(filteredBrands);
	}, [brands, objectType]);

	const handleDelete = (id) => {
		setSelectedTemplate(id);
		setDeleteModalOpen(true);
	};

	const handleEdit = (record) => {
		history.push(`/messagetemplates/${record.id}`);
	};

	const handleSubmitDelete = async () => {
		const result = await deleteMessageTemplate(selectedTemplate);
		setDeleteModalOpen(false);

		if (result === true) {
			DisplayMessage(resources["TemplateWasDeletedWithSuccess"], "success");
			fetchMessageTemplates();
		}

		setSelectedTemplate(null);
	};

	const handleCopy = (rowData) => {
		setSelectedTemplateToCopy(rowData);
		setCopyModalOpen(true);
		setCopyError(null);
	};

	const handleCloseCopy = () => {
		setSelectedTemplateToCopy(null);
		setCopyModalOpen(false);
		setCopyError(null);
	};

	const handleSubmitCopy = async ({ targetOutletId, targetBrandId, targetIsWaitsOnPremiseDependent, targetIsWaitsOnPremise }) => {
		if (!selectedTemplateToCopy) return;

		setCopyLoading(true);

		const result = await copyMessageTemplate(
			JSON.stringify({
				outletId: targetOutletId !== 0 ? targetOutletId : null,
				brandId: targetBrandId !== 0 ? targetBrandId : null,
				isWaitsOnPremiseDependent: targetIsWaitsOnPremiseDependent,
				isWaitsOnPremise: targetIsWaitsOnPremise,
			}),
			selectedTemplateToCopy.id
		);

		setCopyLoading(false);

		if (result.success) {
			setCopyModalOpen(false);
			setSelectedTemplateToCopy(null);

			DisplayMessage(
				resources["MessageTemplateWasCopiedWithSuccess"],
				"success"
			);

			if (selectedOutlet === 0 || selectedOutlet === targetOutletId) {
				await fetchMessageTemplates();
			}

			history.push(`/messagetemplates/${result.data.id}`);
		} else {
			handleCopyError(result);
		}
	};

	const handleCopyError = (result) => {
		if (result.error === "MessageTemplateAlreadyExists") {
			setCopyError("MessageTemplateAlreadyExists");
			DisplayMessage(
				resources["MessageTemplateAlreadyExistsForThisOutlet"],
				"error"
			);
		} else {
			const errorMessage = result.errors
				? result.errors.join(", ")
				: resources["ErrorCopyingMessageTemplate"];
			DisplayMessage(errorMessage, "error");

			setCopyModalOpen(false);
			setSelectedTemplateToCopy(null);
		}
	};

	const filterBrandsByObjectType = (objectType) => {
        if (!brands || !objectType) {
            return brands || [];
        }

        return brands.filter(brand => brand.objectType === objectType);
    };

	 const handleChangeOutlet = e => {
       const selectedOutletId = parseInt(e.target.value);
        
        const selectedOutlet = accessibleMoboOutlets?.find(outlet => outlet.id === selectedOutletId);
        const usedObjectType = selectedOutlet ? selectedOutlet.objectType : objectType;
       	const filteredBrands = filterBrandsByObjectType(usedObjectType);

	   setSelectedOutlet(selectedOutletId);
	   setSelectedBrand(0);
	   setFilteredBrands(filteredBrands);
        
    };

	const tableTheme = useMemo(() => {
		return createTheme({
			...LocalizationTheme(),
			palette: {
				...LocalizationTheme().palette,
				primary: {
					...LocalizationTheme().palette.primary,
					main: "#53B3EA",
				},
			},
		});
	}, []);

	const columns = [
		{
			title: resources["UseCaseDescription"],
			field: "useCaseDescription",
		},
		{
			title: resources["MessageType"],
			field: "messageTypeText",
		},
		{
			title: resources["Outlet"],
			field: "outletDescription",
		},
		{
			title: resources["Brand"],
			field: "brand",
		},
		{
			title: resources["Language"],
			field: "language",
		},
	];

	const hasAccessToMessageTemplates = userPermissions?.some(
		(permission) => permission.id === Permissions.AccessToMessageTemplates
	);

	const baseActions = [
		{
			icon: () => <Edit />,
			tooltip: resources["EditTooltip"],
			onClick: (_, rowData) => handleEdit(rowData),
		},
	];

	const additionalActions = hasAccessToMessageTemplates
		? [
				{
					icon: () => <DeleteOutline />,
					tooltip: resources["Delete"],
					onClick: (_, rowData) => handleDelete(rowData.id),
				},
				{
					icon: () => <FileCopy />,
					tooltip: resources["Copy"],
					onClick: (_, rowData) => handleCopy(data.find((item) => item.id === rowData.id)),
				},
		  ]
		: [];

	const actions = [...baseActions, ...additionalActions];

	const options = {
		emptyRowsWhenPaging: false,
		pageSize: 10,
		pageSizeOptions: [10, 15, 20],
	};

	return (
		<Box>
			<Box className="position-relative py-4">
				<Typography variant="h3">{resources["MessageTemplates"]}</Typography>

				<Box className={"offcentered-loader " + (loading ? "fadeIn" : "fadeOut")}>
					<CircularProgress />
				</Box>

		<Box className={"mt-4 " + (loading ? "fadeOut" : "fadeIn")}>
			<Box display="flex" style={{ marginBottom: "32px" }}>
				<Box style={{ width: "330px", marginRight: "16px" }}>
					<OutletsDropdown
						resources={resources}
						outlets={accessibleMoboOutlets}
						outlet={selectedOutlet}
						onOutletChange={handleChangeOutlet}
						containerClass=""
						id="outlet-dropdown-grid"
						disabled={loading}
					/>
    			</Box>
    
				<Box style={{ width: "330px" }}>
						<BrandsDropdown
							resources={resources}
							brand={selectedBrand}
							brands={filteredBrands}
							filteredByObjectType={true}
							onBrandChange={(e) => setSelectedBrand(parseInt(e.target.value))}
							containerClass=""
							id="brand-dropdown-grid"
							disabled={loading} />
				</Box>
						<Link to={{ pathname: "/messagetemplates/new", }} style={{ textDecoration: "none", marginLeft: "auto" }} >
							<Button
								id="add-message-template"
								variant="contained"
								color="primary" >
								<i className="fas fa-plus mr-2" title={resources["AddNewMessageTemplate"]} />
								{resources["AddNewMessageTemplate"]}
							</Button>
						</Link>
					</Box>

					<Paper elevation={3} className="p-3">
						<ThemeProvider theme={tableTheme}>
							<MaterialTable
								icons={TableIcons}
								title=""
								localization={{
									header: { actions: resources["Actions"] },
									body: {
										emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
										deleteTooltip: resources["DeleteTooltip"],
										editTooltip: resources["EditTooltip"],
									},
									toolbar: {
										searchPlaceholder: resources["Search"],
										searchTooltip: resources["Search"],
									},
									pagination: {
										firstTooltip: resources["FirstTooltip"],
										lastTooltip: resources["LastTooltip"],
										previousTooltip: resources["PreviousTooltip"],
										nextTooltip: resources["NextTooltip"],
										labelRowsPerPage: resources["LabelRowsPerPage"],
										labelRowsSelect: resources["LabelRowsSelect"],
									},
								}}
								data={data}
								columns={columns}
								actions={actions}
								options={options}
							/>
						</ThemeProvider>
					</Paper>
				</Box>

				<DeleteConfirmationModal
					open={deleteModalOpen}
					onClose={() => setDeleteModalOpen(false)}
					onConfirm={handleSubmitDelete}
					resources={resources}
					areYouSureMessage={
						resources["AreYouSureYouWantToDeleteMessageTemplate"]
					}
				/>
				<MessageTemplatesCopyModal
					open={copyModalOpen}
					onClose={handleCloseCopy}
					onConfirm={handleSubmitCopy}
					resources={resources}
					templateDetails={selectedTemplateToCopy}
					accessibleOutlets={accessibleMoboOutlets}
					objectType={objectType}
					loading={copyLoading}
					error={copyError}
					onOutletChange={() => setCopyError(null)}
				/>
			</Box>
		</Box>
	);
};

export default MessageTemplatesTable;
